import React, { useEffect, useState } from 'react';
import Header from 'src/components/common/Header';
import ErrorModal from 'src/components/common/ErrorModal';
import Tabs from 'src/pages/workOrder/form/CreateTab';
import { FileRecord, WorkOrder } from '@twinsketch/topika-model';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ApiService from 'src/services/ApiService';
import { useFetchDataById } from 'src/hook/CustomHook';
import FileService from 'src/services/FileService';
import MinimumWorkOrderForm from 'src/pages/workOrder/form/MinimumWorkOrderForm';
import CreateEditForm from './form/CreateEditForm';
import WorkOrderCoatingWorkForm from './form/reportView/WorkOrderCoatingWork';
import WorkOrderMixingWorkForm from './form/reportView/WorkOrderMixingWork';
import WorkOrderPressWorkForm from './form/reportView/WorkOrderPressWork';
import ROUTES from 'src/constants/routes';
import WorkOrderSlitWorkForm from './form/reportView/WorkOrderSlitWork';
import COAForm from './form/reportView/WorkOrderCoa';
import FileUploadAndListTab from 'src/components/file/workorder/FileUploadAndListTab';
import 'src/styles/workOrder/work-order.css';

const WorkOrderHigherForm: React.FC = () => {
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const { id, tab } = useParams<{ id?: string; tab?: string }>();
  const activeTab = tab || 'thisWorkOrder';

  const [isWorkOrderCreated, setIsWorkOrderCreated] = useState<boolean>(false);
  const [workOrderData, setWorkOrderData] = useState<WorkOrder | null>(null);
  const [files, setFiles] = useState<FileRecord[]>([]);

  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit/');

  const getAllSourceFileTabPairs = (): string[] => {
    if (!workOrderData) return [];

    const all = [
      ...(workOrderData.mixingWorks || []),
      ...(workOrderData.coatingWorks || []),
      ...(workOrderData.pressWorks || []),
      ...(workOrderData.slitWorks || []),
      ...(workOrderData.coas || []),
    ];

    const idTabPairs = all
      .map(work => {
        const fileId = work.extractedFrom?.sourceFileId;
        const tabName = work.extractedFrom?.sourceTabName?.trim();
        return fileId && tabName ? `${fileId}-${tabName}` : null;
      })
      .filter((val): val is string => !!val); // filters out null and empty
    return Array.from(new Set(idTabPairs));
  };

  useEffect(() => {
    setIsWorkOrderCreated(!!id);
  }, [id]);

  useEffect(() => {
    if (id) {
      FileService.getFilesByLinkedId(id)
        .then(fetchedFiles => setFiles(fetchedFiles))
        .catch(err => setError(err.message));
    }
  }, [id]);

  useFetchDataById(ApiService.fetchWorkOrderById, id, setWorkOrderData, setError);

  // Define the base tabs
  const tabs = id
    ? [
        {
          key: 'thisWorkOrder',
          label: 'This Work Order',
          content: isEditMode ? <CreateEditForm /> : <MinimumWorkOrderForm />,
        },
        {
          key: 'files',
          label: 'Files',
          content: (
            <FileUploadAndListTab
              files={files}
              workOrderId={id}
              isWorkOrderCreated={isWorkOrderCreated}
              workOrderData={workOrderData}
              extractedFileIds={getAllSourceFileTabPairs()}
              setWorkOrderData={setWorkOrderData}
              setError={setError}
            />
          ),
        },
        ...(workOrderData?.mixingWorks?.length
          ? [
              {
                key: 'mixingWork',
                label: 'Mixing Work',
                content: (
                  <WorkOrderMixingWorkForm files={files} mixingWorks={workOrderData.mixingWorks} />
                ),
              },
            ]
          : []),
        ...(workOrderData?.coatingWorks?.length
          ? [
              {
                key: 'coatingWork',
                label: 'Coating Work',
                content: (
                  <WorkOrderCoatingWorkForm
                    files={files}
                    coatingWorks={workOrderData.coatingWorks}
                  />
                ),
              },
            ]
          : []),
        ...(workOrderData?.pressWorks?.length
          ? [
              {
                key: 'pressWork',
                label: 'Press Work',
                content: (
                  <WorkOrderPressWorkForm files={files} pressWorks={workOrderData.pressWorks} />
                ),
              },
            ]
          : []),
        ...(workOrderData?.slitWorks?.length
          ? [
              {
                key: 'slitWork',
                label: 'Slit Work',
                content: (
                  <WorkOrderSlitWorkForm files={files} slitWorks={workOrderData.slitWorks} />
                ),
              },
            ]
          : []),
        ...(workOrderData?.coas?.length
          ? [
              {
                key: 'coa',
                label: 'COA',
                content: <COAForm files={files} coaData={workOrderData.coas} />,
              },
            ]
          : []),
      ]
    : [{ key: 'createWorkOrder', label: 'Create Work Order', content: <CreateEditForm /> }];

  const activeTabKey = tabs.find(t => t.key === activeTab)?.key || tabs[0]?.key || 'default';

  const handleTabChange = (key: string) => {
    if (id) navigate(ROUTES.WORK_ORDERS_DETAIL_TAB(id, key));
  };
  return (
    <div>
      <Header />
      <ErrorModal message={error} onClose={() => setError(null)} />
      <div className="content-wrapper">
        <Tabs tabs={tabs} activeKey={activeTabKey} onTabChange={handleTabChange} />
      </div>
    </div>
  );
};

export default WorkOrderHigherForm;
